import {
  createContext,
  HTMLAttributes,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AccepterModalActionType } from "src/brand/EC/components/folder/footer/FolderFooter";
import ActionIcon from "src/core/assets/images/icons/action.svg?react";
import CheckIcon from "src/core/assets/images/icons/checkCircle.svg?react";
import CloseIcon from "src/core/assets/images/icons/close.svg?react";
import CompletedIcon from "src/core/assets/images/icons/completed.svg?react";
import CrayonIcon from "src/core/assets/images/icons/crayon.svg?react";
import CurrentVersionIcon from "src/core/assets/images/icons/currentVersion.svg?react";
import FileVersionIcon from "src/core/assets/images/icons/fileVersion.svg?react";
import MinimizeIcon from "src/core/assets/images/icons/minimize.svg?react";
import ReexamineIcon from "src/core/assets/images/icons/reexamine.svg?react";
import SendIcon from "src/core/assets/images/icons/send.svg?react";
import TrashIcon from "src/core/assets/images/icons/trash.svg?react";
import { ExportPdfFolderAction } from "src/core/components/folder/ExportPdfFolderAction";
import { CloseFileModal } from "src/core/components/folder/footer/CloseFileModal";
import CommentModal from "src/core/components/folder/footer/CommentModal";
import { DeleteFolderModal } from "src/core/components/folder/footer/DeleteFolderModal";
import ReexaminationModal from "src/core/components/folder/footer/ReexaminationModal";
import RenvoiCompletionModal from "src/core/components/folder/footer/RenvoiCompletionModal";
import ValidationModal from "src/core/components/folder/footer/ValidationModal";
import { VersionsModal } from "src/core/components/folder/footer/VersionsModal";
import { Button } from "src/core/components/ui/button";
import { Status } from "src/core/constants/folders";
import { RISK_LEVEL_ICON_CLASSES } from "src/core/constants/risk";
import { Roles } from "src/core/constants/roles";
import { useFolder, useFolderCurrentVersion } from "src/core/contexts/FolderContext";
import { FolderFile, FolderFileDeletionFragment, WorkflowEvent } from "src/core/gql/graphql";
import { useDisclosure } from "src/core/hooks/common/useDisclosure";
import { useFolderRisk } from "src/core/hooks/folder/useFolderRisk";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { usePermissions } from "src/core/hooks/usePermissions";
import { cn } from "src/core/lib/utils";
import { getLastSaveLabel } from "src/core/utils/lastSaveLabel";
import { mapCodeToCustomStatus } from "src/core/utils/status";

type FolderFooterUIContext = {
  vm: {
    folderFile: FolderFile | undefined;
    displayVersions: boolean;
    currentVersion: FolderFile | undefined;
    summaries: FolderFile[];
    disabled: boolean;
    folderState: Status | undefined;
    lastDraftAuditEventComment: string | undefined;
    displayNoMaintaining: boolean;
    displayReexaminationButton: boolean;
    missingFields: number;
    isModulationInProgress: boolean;
  };
  api: {
    openVersionModal: boolean;
    setOpenVersionModal: (open: boolean) => void;
    handleCurrentVersion: () => void;
  };
};

const folderFooterUIContext = createContext<FolderFooterUIContext | undefined>(undefined);

function useFolderFooterUIContext() {
  const context = useContext(folderFooterUIContext);
  if (!context) {
    throw new Error("useFolderFooterUIContext must be used within a FolderFooterUI");
  }
  return context;
}

type RootProps = PropsWithChildren &
  HTMLAttributes<HTMLDivElement> & {
    className?: string;
  };

function Root({ children, className, ...props }: RootProps) {
  const { folderFile, tabCounters, stakeholdersCounter, isModulationInProgress } = useFolder();
  const currentVersion = useFolderCurrentVersion();
  const navigate = useNavigate();
  const paths = useRequiredTenantPaths();

  const summaries = useMemo(
    () => folderFile.folderFile?.currentCustomerFolder?.folderFiles?.summaries ?? [],
    [folderFile.folderFile?.currentCustomerFolder?.folderFiles?.summaries]
  );

  const displayVersions = summaries.length > 1;
  const [openVersionModal, setOpenVersionModal] = useState<boolean>(false);

  const isComplete = folderFile.folderFile?.completionStatus?.complete;

  const folderState = useMemo(
    () => mapCodeToCustomStatus(folderFile.folderFile?.workflowStatus?.state?.code),
    [folderFile.folderFile?.workflowStatus?.state?.code]
  );
  const isDraft = folderState === Status.DRAFT;
  const allowedTransitions = folderFile.folderFile?.allowedTransitions || [];
  const canSubmit = allowedTransitions.some((transition) => transition.action?.code === "SUBMIT");
  const missingFields = useMemo(() => {
    return stakeholdersCounter.global + tabCounters.missions + tabCounters.questionnaire;
  }, [stakeholdersCounter.global, tabCounters.missions, tabCounters.questionnaire]);
  const disabled = tabCounters.global > 0 || !isComplete || !isDraft || !canSubmit;
  const auditEventsWorkflow = folderFile.folderFile?.auditEvents || [];

  const lastDraftAuditEvent = auditEventsWorkflow
    .filter(
      (event) =>
        event.__typename === "WorkflowEvent" &&
        "date" in event &&
        typeof (event as WorkflowEvent).date === "string" &&
        "state" in event &&
        (event as WorkflowEvent).state &&
        typeof (event as WorkflowEvent).state.code === "string" &&
        (event as WorkflowEvent).state.code === "DRAFT"
    )
    .sort(
      (a, b) =>
        new Date((b as WorkflowEvent).date).getTime() -
        new Date((a as WorkflowEvent).date).getTime()
    )[0];

  const lastDraftAuditEventComment =
    lastDraftAuditEvent &&
    "comment" in lastDraftAuditEvent &&
    typeof lastDraftAuditEvent.comment === "string"
      ? lastDraftAuditEvent.comment
      : "";

  const [displayNoMaintaining, setDisplayNoMaintaining] = useState(false);
  const [displayReexaminationButton, setDisplayReexaminationButton] = useState(false);

  useEffect(() => {
    setDisplayNoMaintaining(
      folderFile.folderFile?.currentCustomerFolder?.missions?.some(
        (mission: { __typename?: "Mission"; id: string; isClosable: boolean }) => mission.isClosable
      ) ?? false
    );
    setDisplayReexaminationButton(
      folderFile.folderFile?.workflowStatus?.state?.code !== Status.REJECTED
    );
  }, [folderFile]);

  const vm = useMemo(
    () => ({
      folderFile: folderFile.folderFile as FolderFile | undefined,
      displayVersions,
      currentVersion: currentVersion as FolderFile | undefined,
      summaries: summaries as FolderFile[],
      disabled,
      folderState,
      lastDraftAuditEventComment,
      displayNoMaintaining,
      displayReexaminationButton,
      missingFields,
      isModulationInProgress,
    }),
    [
      folderFile,
      displayVersions,
      currentVersion,
      summaries,
      disabled,
      folderState,
      lastDraftAuditEventComment,
      displayNoMaintaining,
      displayReexaminationButton,
      missingFields,
      isModulationInProgress,
    ]
  );

  const handleCurrentVersion = useCallback(() => {
    if (currentVersion?.id) {
      navigate(paths.folder(currentVersion?.id));
    }
  }, [currentVersion, navigate, paths]);

  const api = useMemo(
    () => ({
      openVersionModal,
      setOpenVersionModal,
      handleCurrentVersion,
    }),
    [openVersionModal, handleCurrentVersion]
  );

  const value = useMemo(() => ({ vm, api }), [vm, api]);

  return (
    <folderFooterUIContext.Provider value={value}>
      <div {...props} className={cn("folder-footer-banner", className)}>
        {children}
      </div>
    </folderFooterUIContext.Provider>
  );
}

type ActionProps = {
  variantVersion?: "link" | "linkWhite";
  withExportPdfFolder?: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
};
const Action = ({
  variantVersion = "link",
  withExportPdfFolder = false,
  open,
  setOpen,
}: ActionProps) => {
  const { vm } = useFolderFooterUIContext();
  const { t } = useTranslation();

  return open ? (
    <div className="action-footer-container">
      <div className="flex flex-row items-center justify-between w-full">
        <div className="font-medium text-lg">{t("folder.footer.action")}</div>
        <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
          <MinimizeIcon />
        </Button>
      </div>
      {withExportPdfFolder && <ExportPdfFolderAction variant="default" />}
      {!vm.displayVersions && !withExportPdfFolder && (
        <div className="font-light folder-footer-after-validation">
          <div>{t("folder.footer.afterValidation.title")}</div>
          <ul className="folder-footer-after-validation-list">
            <li>{t("folder.footer.afterValidation.items.downloadLab")}</li>
            <li>{t("folder.footer.afterValidation.items.navigateVersions")}</li>
          </ul>
        </div>
      )}
      <VersionAction variant="default" />
    </div>
  ) : (
    <Button variant={variantVersion} onClick={() => setOpen(true)}>
      <ActionIcon />
      {t("folder.footer.action")}
    </Button>
  );
};

type VersionActionProps = {
  variant?: "link" | "linkWhite" | "default";
};

const VersionAction = ({ variant = "link" }: VersionActionProps) => {
  const { t } = useTranslation();
  const { vm, api } = useFolderFooterUIContext();

  return (
    <>
      {vm.displayVersions && (
        <Button variant={variant} onClick={() => api.setOpenVersionModal(true)} className="w-full">
          <FileVersionIcon />
          {t("folder.footer.versions")}
        </Button>
      )}

      <VersionsModal
        open={api.openVersionModal}
        setOpen={api.setOpenVersionModal}
        currentVersion={vm.currentVersion as FolderFile | undefined}
        archivedVersions={
          vm.summaries.filter(
            (file: FolderFile) => file.id !== vm.currentVersion?.id
          ) as FolderFile[]
        }
      />
    </>
  );
};

type ValidationActionsProps = {
  variant?: "default" | "secondary";
  onOpenAccepterModal: (type: AccepterModalActionType) => void;
};

const ValidationActions = ({
  variant = "default",
  onOpenAccepterModal,
}: ValidationActionsProps) => {
  const { t } = useTranslation();
  const { vm } = useFolderFooterUIContext();
  const { hasRole } = usePermissions();

  if (
    hasRole(Roles.COLLABORATEUR) ||
    (vm.folderState === Status.VALIDATION_PENDING_HR && hasRole(Roles.EXPERT_COMPTABLE))
  ) {
    return null;
  }

  return (
    <>
      <div className="folder-footer-banner-actions">
        <Button
          variant={variant === "default" ? "cancel" : "secondaryDestructive"}
          onClick={() => onOpenAccepterModal("refuse")}
        >
          <CloseIcon />
          {t("folder.refuser.button")}
        </Button>
        <Button variant={variant} onClick={() => onOpenAccepterModal("accept")}>
          <CheckIcon />
          {t("folder.accepter.button")}
        </Button>
      </div>
    </>
  );
};

type SubmitForValidationActionProps = {
  variant?: "default" | "secondary";
};

const SubmitForValidationAction = ({ variant = "default" }: SubmitForValidationActionProps) => {
  const { t } = useTranslation();
  const { vm } = useFolderFooterUIContext();

  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  return (
    <>
      <div className="folder-footer-actions">
        <Button variant={variant} disabled={vm.disabled} onClick={() => setShowConfirmDialog(true)}>
          <SendIcon className="w-8 h-8 mr-0" />
          {t("folder.footer.bouton")}
        </Button>
      </div>

      <ValidationModal open={showConfirmDialog} setOpen={setShowConfirmDialog} />
    </>
  );
};

const DraftStatusAndRisk = () => {
  const { t } = useTranslation();
  const { vm } = useFolderFooterUIContext();

  return (
    <>
      {!vm.lastDraftAuditEventComment && (
        <div className="folder-footer-status">
          <div className="folder-footer-status-main">{t("folder.footer.completion")}</div>
          <div className="folder-footer-status-sub">
            <Trans i18nKey="folder.footer.risque" />
          </div>
        </div>
      )}
    </>
  );
};

const MissingFields = () => {
  const { t } = useTranslation();
  const { vm } = useFolderFooterUIContext();

  if (vm.lastDraftAuditEventComment) {
    return null;
  }

  return (
    <div className="folder-footer-status">
      {vm.missingFields > 0 ? (
        <div className="rounded-lg bg-red-medium text-gray-xxlight px-20 h-30 flex flex-row items-center justify-center gap-x-10">
          <span className="item-counter-tag bg-gray-xxlight">{vm.missingFields}</span>
          {t("folder.footer.missingFields")}
        </div>
      ) : (
        <div className="rounded-lg bg-blue-completed text-gray-xxlight px-20 h-30 flex flex-row items-center justify-center gap-x-10">
          <CompletedIcon />
          {t("folder.footer.completed")}
        </div>
      )}
    </div>
  );
};

const ValidationStatusAndRisk = () => {
  const { t } = useTranslation();
  const globalRisk = useFolderRisk("global");

  return (
    <div className="folder-footer-status">
      <div className="folder-footer-status-main">{t("folder.status.enValidation")}</div>
      <div className={`folder-footer-status-sub ${RISK_LEVEL_ICON_CLASSES[globalRisk]}`}>
        {t("folder.risk.title")} : {t(`folder.cartographie.globalRisk.${globalRisk.toLowerCase()}`)}
      </div>
    </div>
  );
};

const SendBackToDraftAction = () => {
  const { t } = useTranslation();
  const { vm } = useFolderFooterUIContext();
  const { hasRole } = usePermissions();

  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  if (
    hasRole(Roles.COLLABORATEUR) ||
    (vm.folderState === Status.VALIDATION_PENDING_HR && hasRole(Roles.EXPERT_COMPTABLE))
  ) {
    return null;
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setShowConfirmDialog(true)}>
        <CrayonIcon className="w-4 h-4 mr-2" />
        {t("folder.footer.renvoi")}
      </Button>

      <RenvoiCompletionModal open={showConfirmDialog} setOpen={setShowConfirmDialog} />
    </>
  );
};

const ReturnReasonAction = () => {
  const { t } = useTranslation();
  const { vm } = useFolderFooterUIContext();

  const [showCommentModal, setShowCommentModal] = useState<boolean>(false);

  return (
    <>
      {vm.lastDraftAuditEventComment && (
        <>
          <div
            className="folder-footer-comment-block cursor-pointer"
            onClick={() => setShowCommentModal(true)}
            role="button"
            aria-label={t("folder.renvoi.motif.aria_label")}
          >
            <div className="folder-footer-comment-label">
              <CrayonIcon className="w-8 h-8 mr-10" />
              <div>
                <div>{t("folder.renvoi.motif.line1")}</div>
                <div>{t("folder.renvoi.motif.line2")}</div>
              </div>
            </div>
            <div className="folder-footer-comment-text">{vm.lastDraftAuditEventComment}</div>
          </div>

          <CommentModal
            open={showCommentModal}
            setOpen={setShowCommentModal}
            lastDraftAuditEventComment={vm.lastDraftAuditEventComment}
          />
        </>
      )}
    </>
  );
};

const ReexaminationAction = () => {
  const { t } = useTranslation();
  const [showReexaminationDialog, setShowReexaminationDialog] = useState<boolean>(false);

  return (
    <>
      <Button variant="secondary" onClick={() => setShowReexaminationDialog(true)}>
        <ReexamineIcon />
        {t("folder.footer.reexamination")}
      </Button>

      <ReexaminationModal open={showReexaminationDialog} setOpen={setShowReexaminationDialog} />
    </>
  );
};

type CurrentVersionLinkProps = {
  variant?: "tertiary" | "tertiaryWhite";
};

const CurrentVersionLink = ({ variant = "tertiary" }: CurrentVersionLinkProps) => {
  const { t } = useTranslation();
  const { vm, api } = useFolderFooterUIContext();

  return (
    <>
      {vm.currentVersion && (
        <div className="flex-1 flex justify-center">
          <Button variant={variant} onClick={api.handleCurrentVersion}>
            <CurrentVersionIcon />
            {t("folder.footer.currentVersion")}
          </Button>
        </div>
      )}
    </>
  );
};

const CloseMissionAction = () => {
  const { t } = useTranslation();
  const { vm } = useFolderFooterUIContext();

  const [showCloseDialog, setShowCloseDialog] = useState<boolean>(false);

  return (
    <>
      {vm.displayNoMaintaining && vm.displayReexaminationButton && (
        <Button variant="secondaryDestructive" onClick={() => setShowCloseDialog(true)}>
          <CloseIcon className="w-[14px] h-[14px]" />
          {t("folder.footer.close")}
        </Button>
      )}

      <CloseFileModal open={showCloseDialog} setOpen={setShowCloseDialog} />
    </>
  );
};

type DeleteFolderActionProps = {
  variant?: "linkDestructive" | "linkWhite";
};

const DeleteFolderAction = ({ variant = "linkDestructive" }: DeleteFolderActionProps) => {
  const { t } = useTranslation();

  const { vm } = useFolderFooterUIContext();
  const { open, onOpen, onClose } = useDisclosure(false);

  if (!vm.folderFile) {
    return null;
  }

  return (
    <>
      <Button type="button" variant={variant} size="small" onClick={onOpen}>
        <TrashIcon className="w-[16px] h-[16px]" />
        {t("folder.footer.deleteFolder")}
      </Button>

      <DeleteFolderModal
        open={open}
        onClose={onClose}
        folderFile={vm.folderFile as unknown as FolderFileDeletionFragment}
      />
    </>
  );
};

const LastSaveInfo = () => {
  const { t } = useTranslation();
  const { vm } = useFolderFooterUIContext();
  const [, setTick] = useState(0);

  const lastModification = vm.folderFile?.adminStatus?.lastModification;
  const labelResult = getLastSaveLabel(lastModification ?? undefined);

  useEffect(() => {
    if (!labelResult || labelResult.kind === "onDate") return;
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, [labelResult]);

  if (!lastModification || !labelResult) return null;

  let subLabel: string;
  switch (labelResult.kind) {
    case "onDate":
      subLabel = t("folder.footer.lastSave.onDate", { date: labelResult.dateFormatted });
      break;
    case "seconds":
      subLabel = t("folder.footer.lastSave.ago.seconds");
      break;
    case "minute":
      subLabel = t("folder.footer.lastSave.ago.minute");
      break;
    case "minutes":
      subLabel = t("folder.footer.lastSave.ago.minutes", { count: labelResult.count });
      break;
    case "hour":
      subLabel = t("folder.footer.lastSave.ago.hour");
      break;
    case "hours":
      subLabel = t("folder.footer.lastSave.ago.hours", { count: labelResult.count });
      break;
    case "day":
      subLabel = t("folder.footer.lastSave.ago.day");
      break;
    case "days":
      subLabel = t("folder.footer.lastSave.ago.days", { count: labelResult.count });
      break;
  }

  return (
    <div className="folder-footer-last-save">
      <div>{t("folder.footer.lastSave.title")}</div>
      <div className="font-medium">{subLabel}</div>
    </div>
  );
};

export const FolderFooterUI = {
  Root,
  VersionAction,
  ValidationActions,
  SubmitForValidationAction,
  DraftStatusAndRisk,
  MissingFields,
  ValidationStatusAndRisk,
  SendBackToDraftAction,
  ReturnReasonAction,
  ReexaminationAction,
  CurrentVersionLink,
  CloseMissionAction,
  DeleteFolderAction,
  LastSaveInfo,
  Action,
};
